import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Text, TextArea } from 'grommet';
import PropTypes from 'prop-types';
import { Chat, CircleInformation } from 'grommet-icons';
import {
    fetchResumeComments,
    createResumeComment,
    resolveResumeComment,
} from '../pages/ContactProfile/ResumeActions';

const EXCERPT_MAX_LENGTH = 80;
const MISSING_BLOCK_EXCERPT = '(original text no longer available)';
const DRAFT_PENDING_KEY = 'draft';

const truncate = (text) =>
    text.length > EXCERPT_MAX_LENGTH
        ? `${text.slice(0, EXCERPT_MAX_LENGTH)}…`
        : text;

// Parsed with DOMParser rather than a detached div: a div created by
// document.createElement still belongs to the active document, so assigning
// innerHTML there runs `<img onerror>` style handlers out of resume content the
// student authored. DOMParser output has no browsing context, so nothing loads
// and nothing executes. Resume content is stored unsanitized, so this must stay
// inert.
const buildExcerptsByBlockId = (contentHtml) => {
    const excerpts = new Map();
    if (!contentHtml) return excerpts;

    const parsed = new DOMParser().parseFromString(contentHtml, 'text/html');

    parsed.querySelectorAll('[data-block-id]').forEach((block) => {
        const blockId = block.getAttribute('data-block-id');
        if (!blockId || excerpts.has(blockId)) return;
        excerpts.set(blockId, truncate(block.textContent.trim()));
    });

    return excerpts;
};

export const ResumeComments = ({ documentId, content, activeBlockId }) => {
    const [comments, setComments] = useState([]);
    const [draftText, setDraftText] = useState('');
    // Which single operation is in flight — 'draft' for a new comment, or the
    // id of the comment being resolved. A shared boolean showed progress on the
    // wrong control and left the resolve buttons clickable mid-request.
    const [pendingKey, setPendingKey] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // One parse per content change instead of one per comment per render.
    const excerptsByBlockId = useMemo(
        () => buildExcerptsByBlockId(content),
        [content],
    );

    const blockExcerpt = (blockId) => {
        if (!blockId) return '';
        return excerptsByBlockId.get(blockId) ?? MISSING_BLOCK_EXCERPT;
    };

    useEffect(() => {
        if (!documentId) return;

        let isActive = true;
        (async () => {
            const fetched = await fetchResumeComments(documentId);
            if (isActive) setComments(fetched || []);
        })();

        return () => {
            isActive = false;
        };
    }, [documentId]);

    const reloadComments = async () => {
        const fetched = await fetchResumeComments(documentId);
        if (isMounted.current) setComments(fetched || []);
    };

    const runCommentAction = async (key, action, failureMessage) => {
        setPendingKey(key);
        setErrorMessage('');
        try {
            await action();
            await reloadComments();
        } catch (error) {
            console.log(error);
            if (isMounted.current) setErrorMessage(failureMessage);
        }
        if (isMounted.current) setPendingKey(null);
    };

    const handleAddComment = async () => {
        if (!activeBlockId || !draftText.trim()) return;

        await runCommentAction(
            DRAFT_PENDING_KEY,
            async () => {
                await createResumeComment(documentId, activeBlockId, draftText);
                if (isMounted.current) setDraftText('');
            },
            'Could not save that comment. Please try again.',
        );
    };

    const handleResolve = (commentId) =>
        runCommentAction(
            commentId,
            () => resolveResumeComment(commentId),
            'Could not resolve that comment. Please try again.',
        );

    const unresolvedCount = comments.filter(
        (c) => !c.attributes.resolved,
    ).length;

    return (
        <Box gap="small">
            <Box direction="row" align="center" gap="xsmall">
                <Chat size="small" />
                <Text weight={500}>
                    Comments{unresolvedCount > 0 && ` (${unresolvedCount})`}
                </Text>
            </Box>
            <Box direction="row" gap="small" align="center">
                <Box flex>
                    <TextArea
                        value={draftText}
                        disabled={!activeBlockId}
                        placeholder={
                            activeBlockId
                                ? 'Add a comment on the selected line…'
                                : 'Click into a line in the resume to comment on it'
                        }
                        onChange={(event) => setDraftText(event.target.value)}
                    />
                </Box>
                <Button
                    label="Comment"
                    disabled={
                        !activeBlockId || !draftText.trim() || !!pendingKey
                    }
                    busy={pendingKey === DRAFT_PENDING_KEY}
                    onClick={handleAddComment}
                />
            </Box>
            {errorMessage && (
                <Text size="small" color="status-critical" role="alert">
                    {errorMessage}
                </Text>
            )}
            {comments.length > 0 && (
                <Box gap="xsmall">
                    {comments.map((comment) => (
                        <Box
                            key={comment.id}
                            pad="small"
                            round="6px"
                            background={
                                comment.attributes.resolved
                                    ? 'light-2'
                                    : 'enrich-grey'
                            }
                            gap="xsmall">
                            <Text size="small" color="dark-4">
                                <i>
                                    &quot;
                                    {blockExcerpt(comment.attributes.blockId)}
                                    &quot;
                                </i>
                            </Text>
                            <Text>{comment.attributes.text}</Text>
                            <Box
                                direction="row"
                                justify="between"
                                align="center">
                                {comment.attributes.resolved ? (
                                    <Box
                                        direction="row"
                                        gap="xsmall"
                                        align="center">
                                        <CircleInformation size="small" />
                                        <Text size="small">Resolved</Text>
                                    </Box>
                                ) : (
                                    <Button
                                        size="small"
                                        label="Mark resolved"
                                        busy={pendingKey === comment.id}
                                        disabled={!!pendingKey}
                                        onClick={() =>
                                            handleResolve(comment.id)
                                        }
                                    />
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

ResumeComments.propTypes = {
    documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    content: PropTypes.string,
    activeBlockId: PropTypes.string,
};
